import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';

type Props = {
  triggerNode: React.ReactNode;
  onSendRequest: (data: FormData) => Promise<void>;
  isLoading?: boolean;
};

const formSchema = z.object({
  message: z.string().min(1, 'Message is required'),
});

type FormData = z.infer<typeof formSchema>;

const RequestCertifiedLevelDialog = ({ triggerNode, onSendRequest }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      message: 'I want to get certified',
    },
  });

  const onClose = () => {
    setIsOpen(false);
    form.reset();
  };

  const handleSendRequest = async (data: FormData) => {
    await onSendRequest(data);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{triggerNode}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className="gap-1.5">
          <DialogTitle>Interested in getting Certified?</DialogTitle>
          <DialogDescription>Send us a message and we’ll get in touch shortly.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSendRequest)} className="space-y-6">
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[106px] resize-none" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="sm:justify-between">
              <Button variant="secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" isLoading={form.formState.isSubmitting} disabled={form.formState.isSubmitting}>
                Send request
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RequestCertifiedLevelDialog;
